import { Fragment, useId, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Plus,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { KeyValueEditor } from "@/components/itsm-config/key-value-editor";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { useDraft } from "@/components/itsm-config/draft-context";
import {
  useJiraStatuses,
  useSnowAssignmentGroups,
  useSnowFields,
  useSnowMandatoryFields,
  useSnowTables,
  useSnowTicketTypes,
  type MandatoryFieldMeta,
} from "@/hooks/use-itsm-metadata";
import {
  mandatoryRowProblems,
  type MandatoryRowProblems,
} from "@/lib/itsm-config/mandatory-fields";
import type {
  ExternalCategoryOption,
  MandatoryFieldDefault,
  MandatoryFieldStrategy,
  ProviderConfig,
  Transition,
} from "@/lib/types/itsm-config";

interface BaseProps {
  tenantId: string;
  credentialId: string;
  provider: string;
  providerConfig: ProviderConfig;
}

/** Write a provider-config patch into the page draft (persisted on Save).
 *  The optional second arg (a legacy toast message) is ignored. */
function useSave(_props: BaseProps) {
  const { update } = useDraft();
  return (patch: Partial<ProviderConfig>, _msg?: string) => { update(patch); };
}

/** Jira: turn Jira issue links into Harmony ticket links (tenant-wide). */
export function JiraLinkedTicketsSection(props: BaseProps) {
  const save = useSave(props);
  const enabled = props.providerConfig.sync_linked_tickets ?? false;
  const titleId = useId();
  const noteId = useId();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked tickets</CardTitle>
        <CardDescription>
          Carry Jira issue links over to Harmony. Applies to every project on
          this integration, including manual import jobs unless a job
          overrides it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3 rounded-md border px-4 py-3">
          <div className="flex-1 space-y-0.5">
            <p id={titleId} className="text-sm font-medium">
              Sync linked tickets
            </p>
            <p id={noteId} className="text-muted-foreground text-sm">
              {enabled
                ? "On — Jira issue links become Harmony ticket links, and a " +
                  "newly imported ticket is routed to the same desk as its " +
                  "earliest-created linked ticket already in Harmony. " +
                  "Tickets already in Harmony are not moved."
                : "Off — Jira issue links are ignored and each ticket is " +
                  "routed on its own."}
            </p>
          </div>
          <Switch
            checked={enabled}
            aria-labelledby={titleId}
            aria-describedby={noteId}
            onCheckedChange={(v) => { save({ sync_linked_tickets: v }); }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/** Jira: workflow transitions keyed by target status, comment posted on move. */
export function JiraTransitionsSection(props: BaseProps) {
  const save = useSave(props);
  const transitions = props.providerConfig.transitions ?? {};
  const entries = Object.entries(transitions);
  const [newStatus, setNewStatus] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const statuses = useJiraStatuses(props.tenantId, props.credentialId);

  const persist = (next: Record<string, Transition>) => {
    save(
      { transitions: Object.keys(next).length ? next : null },
      "Transitions saved",
    );
  };
  const setComment = (status: string, comment: string) => {
    persist({
      ...transitions,
      // eslint-disable-next-line security/detect-object-injection -- status key comes from our own transitions map
      [status]: { ...(transitions[status] ?? {}), comment: comment || null },
    });
  };
  const setFields = (status: string, fields: Record<string, string>) => {
    persist({
      ...transitions,
      [status]: {
        // eslint-disable-next-line security/detect-object-injection -- status key comes from our own transitions map
        ...(transitions[status] ?? {}),
        fields: Object.keys(fields).length ? fields : null,
      },
    });
  };
  const removeStatus = (status: string) => {
    const { [status]: _omit, ...next } = transitions;
    persist(next);
  };
  const addStatus = () => {
    const status = newStatus.trim();
    if (!status || status in transitions) return;
    persist({ ...transitions, [status]: { comment: null } });
    setNewStatus("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Jira transitions</CardTitle>
        <CardDescription>
          When Harmony moves a ticket to a target status, post a comment and/or
          set fields as part of the Jira workflow transition.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target status</TableHead>
              <TableHead>Comment</TableHead>
              <TableHead className="w-32" />
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground text-center text-sm">
                  No transitions
                </TableCell>
              </TableRow>
            ) : (
              entries.map(([status, t]) => (
                <Fragment key={status}>
                  <TableRow>
                    <TableCell className="font-mono text-xs">{status}</TableCell>
                    <TableCell>
                      <Input
                        value={t.comment ?? ""}
                        onChange={(e) => { setComment(status, e.target.value); }}
                        placeholder="Resolved via Harmony"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => {
                          setExpanded(expanded === status ? null : status);
                        }}
                      >
                        <ChevronsUpDown className="mr-1 h-3.5 w-3.5" />
                        Fields
                        {t.fields && Object.keys(t.fields).length > 0
                          ? ` (${Object.keys(t.fields).length})`
                          : ""}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => { removeStatus(status); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded === status && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-muted/30">
                        <p className="text-muted-foreground mb-2 text-xs">
                          Fields set during this transition (Jira field id →
                          value).
                        </p>
                        <KeyValueEditor
                          value={(t.fields ?? {}) as Record<string, string>}
                          onChange={(m) => { setFields(status, m); }}
                          keyLabel="Field id"
                          valueLabel="Value"
                          keyPlaceholder="resolution"
                          valuePlaceholder="Done"
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <div className="flex items-center gap-2">
          <div className="w-[220px]">
            <MetadataSelect
              options={statuses.data?.options ?? []}
              isLoading={statuses.isLoading}
              value={newStatus}
              onChange={setNewStatus}
              placeholder="Add target status..."
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!newStatus.trim() || newStatus.trim() in transitions}
            onClick={addStatus}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Per-table close-field editor. Field name is a metadata dropdown (SNOW
 *  fields for the table); value stays free-text. Split into its own
 *  component so the per-table `useSnowFields` hook obeys the rules of hooks. */
function ServiceNowTableFields({
  tenantId,
  credentialId,
  table,
  fields,
  onChange,
}: {
  tenantId: string;
  credentialId: string;
  table: string;
  fields: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const fieldMeta = useSnowFields(tenantId, credentialId, table);
  const options = fieldMeta.data?.fields ?? [];
  const [newField, setNewField] = useState("");
  const entries = Object.entries(fields);

  const setVal = (k: string, v: string) => { onChange({ ...fields, [k]: v }); };
  const removeField = (k: string) => {
    const { [k]: _omit, ...next } = fields;
    onChange(next);
  };
  const addField = () => {
    const f = newField.trim();
    if (!f || f in fields) return;
    onChange({ ...fields, [f]: "" });
    setNewField("");
  };
  const labelFor = (k: string) =>
    options.find((o) => o.value === k)?.label ?? k;

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Value</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground text-center text-sm">
                No fields
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell className="font-mono text-xs">{labelFor(k)}</TableCell>
                <TableCell>
                  <Input
                    value={v}
                    onChange={(e) => { setVal(k, e.target.value); }}
                    className="h-8"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => { removeField(k); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center gap-2">
        <div className="w-[220px]">
          <MetadataSelect
            options={options}
            isLoading={fieldMeta.isLoading}
            value={newField}
            onChange={setNewField}
            placeholder="Add a field..."
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!newField.trim() || newField.trim() in fields}
          onClick={addField}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

/** ServiceNow: make ServiceNow the ticket's home in Slack and Teams (per
 *  integration). Ticket messages of a ticket synced to this integration show
 *  its number and open it there; tickets with no ServiceNow copy keep
 *  Harmony's. */
export function ServiceNowMainTicketingSystemSection(props: BaseProps) {
  const save = useSave(props);
  const enabled = props.providerConfig.main_ticketing_system ?? false;
  const titleId = useId();
  const noteId = useId();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Main ticketing system</CardTitle>
        <CardDescription>
          Which ticket Slack and Teams ticket messages show and open, both to
          the requester and in the desk channel. Applies to every desk that
          syncs tickets to this ServiceNow integration.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3 rounded-md border px-4 py-3">
          <div className="flex-1 space-y-0.5">
            <p id={titleId} className="text-sm font-medium">
              ServiceNow is the main ticketing system
            </p>
            <p id={noteId} className="text-muted-foreground text-sm">
              {enabled
                ? "On — Slack and Teams ticket messages show the ServiceNow " +
                  "number as the ticket ID, and \"View Ticket\" opens the " +
                  "ServiceNow ticket. Until ServiceNow returns the number " +
                  "they show the Harmony ticket."
                : "Off — Slack and Teams ticket messages show the Harmony " +
                  "ticket and open it in Harmony."}
            </p>
          </div>
          <Switch
            checked={enabled}
            aria-labelledby={titleId}
            aria-describedby={noteId}
            onCheckedChange={(v) => { save({ main_ticketing_system: v }); }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/** ServiceNow: copy the requester's sys_user location onto records we create.
 *  ServiceNow only derives location from the caller on its own form, so
 *  records created over the API are otherwise left without one. Off by
 *  default: some instances govern location themselves or make it read-only. */
export function ServiceNowRequesterLocationSection(props: BaseProps) {
  const save = useSave(props);
  const enabled = props.providerConfig.populate_requester_location ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Requester location</CardTitle>
        <CardDescription>
          Fill a new ticket&apos;s location from the requester&apos;s
          ServiceNow user record. Skipped when that user has no location.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            onCheckedChange={(v) => { save({ populate_requester_location: v }); }}
          />
          <Label className="text-sm">Set location from the requester</Label>
        </div>
      </CardContent>
    </Card>
  );
}

/** ServiceNow: the group that catches tickets arriving without a team.
 *
 *  Outbound normally mirrors the assigned team's name into
 *  assignment_group. A ticket with no team has nothing to mirror and would
 *  land on whatever routing the instance applies on insert; this names
 *  where those go instead. An export rule's own scope still overrides it. */
export function ServiceNowDefaultGroupSection(props: BaseProps) {
  const save = useSave(props);
  const groups = useSnowAssignmentGroups(props.tenantId, props.credentialId);
  const value = props.providerConfig.default_assignment_group ?? "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default assignment group</CardTitle>
        <CardDescription>
          Where tickets go when they carry no team. A ticket that has one
          still routes to its team&apos;s group. Leave empty to let
          ServiceNow route them itself.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <div className="max-w-sm flex-1">
            <MetadataSelect
              options={groups.data?.options ?? []}
              isLoading={groups.isLoading}
              value={value}
              onChange={(v) => { save({ default_assignment_group: v }); }}
              placeholder="Select an assignment group..."
            />
          </div>
          {value && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { save({ default_assignment_group: null }); }}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const MANDATORY_STRATEGIES: {
  value: MandatoryFieldStrategy;
  label: string;
  description: string;
}[] = [
  {
    value: "predefined",
    label: "Predefined choice",
    description: "Always sends the value below. The LLM is never asked.",
  },
  {
    value: "likely",
    label: "Likely choice",
    description:
      "Sends the value below unless the ticket explicitly says otherwise, " +
      "in which case the LLM picks.",
  },
  {
    value: "llm",
    label: "LLM only",
    description: "The LLM picks from the field's options for every ticket.",
  },
];

function describeSources(meta: MandatoryFieldMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.sources.some((s) => s.kind === "dictionary")) {
    return "Always mandatory";
  }
  const policies = meta.sources
    .map((s) => s.short_description?.trim() ?? "")
    .filter(Boolean);
  return policies.length
    ? `Mandatory by UI policy: ${policies.join("; ")}`
    : null;
}

/** One mandatory-field row. Its own component so the per-table metadata hook
 *  obeys the rules of hooks. */
function MandatoryFieldRow({
  tenantId,
  credentialId,
  index,
  row,
  problems,
  strategyNoteId,
  tableOptions,
  tablesLoading,
  onChange,
  onRemove,
}: {
  tenantId: string;
  credentialId: string;
  index: number;
  row: MandatoryFieldDefault;
  problems: MandatoryRowProblems;
  /** Id prefix of the strategy explanations, suffixed with the strategy. */
  strategyNoteId: string;
  tableOptions: { value: string; label: string }[];
  tablesLoading: boolean;
  onChange: (next: MandatoryFieldDefault) => void;
  onRemove: () => void;
}) {
  const fieldsMeta = useSnowMandatoryFields(tenantId, credentialId, row.table);
  const fields = fieldsMeta.data?.fields ?? [];
  const meta = fields.find((f) => f.element === row.field);
  // A new row starts empty; flag what is missing once the admin has edited
  // it (Save names any row still incomplete).
  const [touched, setTouched] = useState(false);
  const baseId = useId();
  const rowId = `${baseId}-row`;
  const tableId = `${baseId}-table`;
  const fieldId = `${baseId}-field`;
  const strategyId = `${baseId}-strategy`;
  const valueId = `${baseId}-value`;
  const fieldNoteId = `${baseId}-field-note`;
  const fieldErrorId = `${baseId}-field-error`;
  const valueErrorId = `${baseId}-value-error`;
  const rowName = `Row ${String(index + 1)}`;

  const needsValue = row.strategy !== "llm";
  const fieldError = !touched
    ? null
    : problems.missingField
      ? "Pick a field."
      : problems.duplicate
        ? "Another row already sets this field."
        : null;
  const valueError =
    touched && problems.missingValue ? "Required for this strategy." : null;
  const fieldNote = fieldsMeta.isError
    ? "Could not read this table's mandatory fields; type the element name."
    : describeSources(meta);
  const fieldDescribedBy =
    [fieldError && fieldErrorId, fieldNote && fieldNoteId]
      .filter(Boolean)
      .join(" ") || undefined;

  const change = (next: MandatoryFieldDefault) => {
    setTouched(true);
    onChange(next);
  };

  return (
    <div
      role="group"
      aria-labelledby={rowId}
      className="grid grid-cols-1 items-start gap-2 border-b pb-3 last:border-b-0 lg:grid-cols-[150px_minmax(160px,1fr)_160px_minmax(160px,1fr)_auto]"
    >
      <span id={rowId} className="sr-only">
        {row.field ? `${rowName}: ${row.table} ${row.field}` : rowName}
      </span>
      <div className="space-y-1">
        <Label id={tableId} className="text-xs">
          Table
        </Label>
        <MetadataSelect
          aria-labelledby={`${tableId} ${rowId}`}
          options={tableOptions}
          isLoading={tablesLoading}
          value={row.table}
          onChange={(v) => {
            if (v === row.table) return;
            change({ ...row, table: v, field: "", value: needsValue ? "" : null });
          }}
          placeholder="incident"
        />
      </div>
      <div className="space-y-1">
        <Label id={fieldId} className="text-xs">
          Field
        </Label>
        <MetadataSelect
          aria-labelledby={`${fieldId} ${rowId}`}
          aria-describedby={fieldDescribedBy}
          aria-invalid={fieldError ? true : undefined}
          options={fields.map((f) => ({ value: f.element, label: f.label }))}
          isLoading={fieldsMeta.isLoading}
          value={row.field}
          onChange={(v) => {
            if (v === row.field) return;
            change({ ...row, field: v, value: needsValue ? "" : null });
          }}
          placeholder="Select a field..."
        />
        {fieldError && (
          <p id={fieldErrorId} className="text-destructive text-xs">
            {fieldError}
          </p>
        )}
        {fieldNote && (
          <p id={fieldNoteId} className="text-muted-foreground text-xs">
            {fieldNote}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label id={strategyId} className="text-xs">
          Strategy
        </Label>
        <Select
          value={row.strategy}
          onValueChange={(v) => {
            const strategy = v as MandatoryFieldStrategy;
            change({
              ...row,
              strategy,
              value: strategy === "llm" ? null : (row.value ?? ""),
            });
          }}
        >
          <SelectTrigger
            size="sm"
            className="w-full"
            aria-labelledby={`${strategyId} ${rowId}`}
            aria-describedby={`${strategyNoteId}-${row.strategy}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MANDATORY_STRATEGIES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        {!needsValue ? (
          <>
            <span aria-hidden className="block text-xs">
              Value
            </span>
            <p className="text-muted-foreground flex h-8 items-center text-sm">
              Chosen by the LLM
            </p>
          </>
        ) : (
          <>
            <Label id={valueId} className="text-xs">
              Value
            </Label>
            {meta?.choices.length ? (
              <MetadataSelect
                aria-labelledby={`${valueId} ${rowId}`}
                aria-describedby={valueError ? valueErrorId : undefined}
                aria-invalid={valueError ? true : undefined}
                options={meta.choices}
                value={row.value ?? ""}
                onChange={(v) => {
                  change({ ...row, value: v });
                }}
                placeholder="Select a value..."
              />
            ) : (
              <Input
                aria-labelledby={`${valueId} ${rowId}`}
                aria-describedby={valueError ? valueErrorId : undefined}
                aria-invalid={valueError ? true : undefined}
                value={row.value ?? ""}
                onChange={(e) => {
                  change({ ...row, value: e.target.value });
                }}
                placeholder={
                  meta?.reference
                    ? `sys_id of a ${meta.reference} record`
                    : "Value"
                }
                className="h-8"
              />
            )}
            {valueError && (
              <p id={valueErrorId} className="text-destructive text-xs">
                {valueError}
              </p>
            )}
          </>
        )}
      </div>
      <div className="space-y-1">
        <span aria-hidden className="hidden text-xs lg:block">
          &nbsp;
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          data-mandatory-remove
          onClick={onRemove}
          aria-label={
            row.field
              ? `Remove ${row.table} ${row.field}`
              : `Remove ${rowName.toLowerCase()}`
          }
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** ServiceNow: how outbound sync fills fields the instance makes mandatory.
 *
 *  Before creating a record, outbound works out every field ServiceNow would
 *  require on it (dictionary-mandatory, or required by a UI policy whose
 *  conditions match the new record) and fills the blank ones. Each row picks
 *  how one field is filled; a mandatory field without a row is left to the
 *  LLM, since a blank mandatory field leaves the ServiceNow record unusable. */
export function ServiceNowMandatoryFieldsSection(props: BaseProps) {
  const save = useSave(props);
  const rows = props.providerConfig.mandatory_field_defaults ?? [];
  const problems = mandatoryRowProblems(rows);
  const tables = useSnowTicketTypes(props.tenantId, props.credentialId);
  const titleId = useId();
  const legendId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);

  const persist = (next: MandatoryFieldDefault[]) => {
    save({ mandatory_field_defaults: next.length ? next : null });
  };

  /** Keep keyboard focus in the list after a row disappears: the row that
   *  took its place, else the one before it, else "Add field". */
  const remove = (index: number) => {
    persist(rows.filter((_, i) => i !== index));
    requestAnimationFrame(() => {
      const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
        "[data-mandatory-remove]",
      );
      const target = buttons?.item(Math.min(index, buttons.length - 1));
      (target ?? addRef.current)?.focus();
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>Mandatory field defaults</CardTitle>
        <CardDescription>
          Fields ServiceNow requires on a new ticket, from its dictionary or
          from a UI policy matching the ticket, are always filled. Choose how
          for each field; any mandatory field without a row is chosen by the
          LLM.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl
          id={legendId}
          className="text-muted-foreground grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs"
        >
          {MANDATORY_STRATEGIES.map((s) => (
            <Fragment key={s.value}>
              <dt className="text-foreground font-medium">{s.label}</dt>
              <dd id={`${legendId}-${s.value}`}>{s.description}</dd>
            </Fragment>
          ))}
        </dl>
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No defaults. Every mandatory field is chosen by the LLM.
          </p>
        ) : (
          <div
            ref={listRef}
            role="group"
            aria-labelledby={titleId}
            className="space-y-3"
          >
            {rows.map((row, index) => (
              <MandatoryFieldRow
                // eslint-disable-next-line react-x/no-array-index-key -- positional row
                key={index}
                tenantId={props.tenantId}
                credentialId={props.credentialId}
                index={index}
                row={row}
                // eslint-disable-next-line security/detect-object-injection -- index from map over the same rows
                problems={problems[index]}
                strategyNoteId={legendId}
                tableOptions={tables.data?.options ?? []}
                tablesLoading={tables.isLoading}
                onChange={(next) => {
                  persist(rows.map((r, i) => (i === index ? next : r)));
                }}
                onRemove={() => {
                  remove(index);
                }}
              />
            ))}
          </div>
        )}
        <Button
          ref={addRef}
          variant="outline"
          size="sm"
          onClick={() => {
            persist([
              ...rows,
              { table: "incident", field: "", strategy: "likely", value: "" },
            ]);
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add field
        </Button>
      </CardContent>
    </Card>
  );
}

/** ServiceNow: close fields keyed by table (each a field->value map). */
export function ServiceNowCloseFieldsSection(props: BaseProps) {
  const save = useSave(props);
  const closeFields = props.providerConfig.close_fields ?? {};
  const tableEntries = Object.entries(closeFields);
  const [newTable, setNewTable] = useState("");
  const tables = useSnowTables(props.tenantId, props.credentialId);

  const addTable = () => {
    const table = newTable.trim();
    if (!table || table in closeFields) return;
    save(
      { close_fields: { ...closeFields, [table]: {} } },
      `Table ${table} added`,
    );
    setNewTable("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Close fields</CardTitle>
        <CardDescription>
          Fields set on resolve/close, per ServiceNow table.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tableEntries.length === 0 && (
          <p className="text-muted-foreground text-sm">No close fields.</p>
        )}
        {tableEntries.map(([table, fields]) => (
          <div key={table} className="space-y-1">
            <p className="text-sm font-medium">{table}</p>
            <ServiceNowTableFields
              tenantId={props.tenantId}
              credentialId={props.credentialId}
              table={table}
              fields={fields}
              onChange={(m) =>
                { save(
                  { close_fields: { ...closeFields, [table]: m } },
                  `Close fields for ${table} saved`,
                ); }
              }
            />
          </div>
        ))}
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">Add a table</p>
          <div className="flex items-center gap-2">
            <div className="w-[220px]">
              <MetadataSelect
                options={tables.data?.options ?? []}
                isLoading={tables.isLoading}
                value={newTable}
                onChange={setNewTable}
                placeholder="Select a table..."
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!newTable.trim() || newTable.trim() in closeFields}
              onClick={addTable}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ServiceNow: external category vocabulary (category → optional
 * sub-categories). Auto-tagging classifies tickets against it and outbound
 * sync sends the chosen `value` (the SNOW sys_choice value). Two levels —
 * what ServiceNow needs; deeper nesting is editable via the raw config.
 */
export function ServiceNowExternalCategoriesSection(props: BaseProps) {
  const save = useSave(props);
  const categories = props.providerConfig.external_categories ?? [];
  // Which categories are expanded (default collapsed — the list is long).
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };
  const allExpanded =
    categories.length > 0 && expanded.size === categories.length;
  const toggleAll = () => {
    setExpanded(
      allExpanded ? new Set() : new Set(categories.map((_, i) => i)),
    );
  };
  const persist = (next: ExternalCategoryOption[]) => {
    save({ external_categories: next.length ? next : null });
  };
  const addCategory = () => {
    setExpanded((prev) => new Set(prev).add(categories.length));
    persist([...categories, { value: "", label: "", sub_categories: null }]);
  };
  const removeCategory = (ci: number) => {
    persist(categories.filter((_, idx) => idx !== ci));
    // Reindex expanded set so open state follows the surviving rows.
    setExpanded((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < ci) next.add(i);
        else if (i > ci) next.add(i - 1);
      });
      return next;
    });
  };
  const patchCat = (i: number, patch: Partial<ExternalCategoryOption>) => {
    persist(categories.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };
  const patchSub = (
    ci: number,
    si: number,
    patch: Partial<ExternalCategoryOption>,
  ) => {
    persist(
      categories.map((c, idx) =>
        idx === ci
          ? {
              ...c,
              sub_categories: (c.sub_categories ?? []).map((s, sidx) =>
                sidx === si ? { ...s, ...patch } : s,
              ),
            }
          : c,
      ),
    );
  };
  const removeSub = (ci: number, si: number) => {
    persist(
      categories.map((c, idx) => {
        if (idx !== ci) return c;
        const next = (c.sub_categories ?? []).filter((_, sidx) => sidx !== si);
        return { ...c, sub_categories: next.length ? next : null };
      }),
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5">
            <CardTitle>External categories</CardTitle>
            <CardDescription>
              Category vocabulary for this ServiceNow instance. Auto-tagging
              classifies tickets against it; outbound sync sends the chosen
              value (the SNOW sys_choice value, not the label).
            </CardDescription>
          </div>
          {categories.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 shrink-0 text-xs"
              onClick={toggleAll}
            >
              {allExpanded ? (
                <ChevronsDownUp className="mr-1 h-3.5 w-3.5" />
              ) : (
                <ChevronsUpDown className="mr-1 h-3.5 w-3.5" />
              )}
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {categories.length === 0 && (
          <p className="text-muted-foreground text-sm">No categories.</p>
        )}
        {categories.map((cat, ci) => {
          const isOpen = expanded.has(ci);
          const subCount = (cat.sub_categories ?? []).length;
          return (
            // eslint-disable-next-line react-x/no-array-index-key -- rows are positional; value/label may be blank or duplicated while editing
            <div key={ci} className="rounded-md border px-2 py-1.5">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  title={isOpen ? "Collapse" : "Expand"}
                  onClick={() => { toggle(ci); }}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
                <Input
                  placeholder="Value (sent to provider)"
                  className="h-8 w-56 font-mono text-xs"
                  value={cat.value}
                  onChange={(e) => { patchCat(ci, { value: e.target.value }); }}
                />
                <Input
                  placeholder="Label"
                  className="h-8 w-56"
                  value={cat.label}
                  onChange={(e) => { patchCat(ci, { label: e.target.value }); }}
                />
                {!isOpen && subCount > 0 && (
                  <span className="text-muted-foreground shrink-0 font-mono text-[11px]">
                    {subCount} sub{subCount === 1 ? "" : "s"}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => { removeCategory(ci); }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {isOpen && (
                <div className="border-muted mt-1.5 mb-1 ml-3.5 space-y-1.5 border-l-2 pl-4">
                  {(cat.sub_categories ?? []).map((sub, si) => (
                    // eslint-disable-next-line react-x/no-array-index-key -- positional row
                    <div key={si} className="flex items-center gap-2">
                      <Input
                        placeholder="Sub value"
                        className="h-8 w-52 font-mono text-xs"
                        value={sub.value}
                        onChange={(e) => { patchSub(ci, si, { value: e.target.value }); }}
                      />
                      <Input
                        placeholder="Sub label"
                        className="h-8 w-52"
                        value={sub.label}
                        onChange={(e) => { patchSub(ci, si, { label: e.target.value }); }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => { removeSub(ci, si); }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      patchCat(ci, {
                        sub_categories: [
                          ...(cat.sub_categories ?? []),
                          { value: "", label: "", sub_categories: null },
                        ],
                      });
                    }}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Sub-category
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        <Button variant="outline" size="sm" onClick={addCategory}>
          <Plus className="mr-1 h-4 w-4" />
          Add category
        </Button>
      </CardContent>
    </Card>
  );
}
